import { z } from "zod";

/**
 * Shared DTO cho Admin Group Health Score & Churn Watchlist (US-AE10.F5-0037 · @UC9094
 * group-health-churn-watchlist). MỘT NGUỒN SỰ THẬT cho api (S01) + admin web (S03).
 *
 * Projection CHỈ-ĐỌC on-the-fly từ nguồn có sẵn (Group / GroupMember / AuthSession / LedgerTransaction /
 * AccountBalance) — DTO chỉ mô tả read-only aggregate; KHÔNG field mutate, KHÔNG cột healthScore. NO migration.
 * Khác AF4.4 (0017 group-financial-health = health tài chính 1-nhóm khi điều tra); F5 = xếp hạng TOÀN HỆ
 * (health score 3-trục + tier) + churn watchlist cho growth ops.
 *
 * BẤT BIẾN (invariants):
 * - C2/PII: ownerRef danh tính owner redact theo vai (SUPER_ADMIN/SUPPORT thấy; OPS/READ_ONLY → null → UI "—").
 *   totalBalanceMinor redact theo vai (SUPER_ADMIN/OPS thấy; SUPPORT/READ_ONLY → null). Aggregate score/tier
 *   luôn hiển thị (không PII).
 * - AC3 no-send: suggestedActions = mảng {type, deepLink} — CHỈ deep-link tới màn AF13.1/detail. KHÔNG payload
 *   gửi broadcast; board KHÔNG có endpoint dispatch. Người gửi ở màn broadcast (tách feature).
 * - B1: tiền BigInt minor → string (no-float). score/sub-scores = number nguyên [0..100] (KHÔNG phải tiền).
 * - `.strict()` mọi object đọc — chống field lạ trên wire. Cursor envelope `{ data, pageInfo }`.
 *
 * Công thức/ngưỡng (Decision Record trong task PROGRESS.md — config-driven SystemSetting, default trung tính):
 * - score = round(engagement*wE + financial*wF + memberGrowth*wG), weights chuẩn hoá Σ=1 (default đều).
 * - tier: healthy ≥ 70 · at-risk 40..69 · critical < 40 (config threshold).
 * - watchlist: no_txn ≥ 30 ngày | active member drop ≥ 25% (config threshold).
 */

// ============================================================
// @UC9094 — Health score tier + axes (AC1)
// ============================================================

/** Bậc sức khoẻ (3 mức) — dẫn xuất từ score theo ngưỡng config. UI badge màu + nhãn. */
export const groupHealthTierSchema = z.enum(["healthy", "at_risk", "critical"]);
export type GroupHealthTier = z.infer<typeof groupHealthTierSchema>;

/**
 * Health score 1 nhóm (@UC9094 AC1). score = tổng hợp 3 trục [0..100] (số nguyên — KHÔNG phải tiền).
 * trend = deltaPct so kỳ trước ([-100..], âm=giảm). ownerRef/totalBalanceMinor redact theo vai (PII/money).
 */
export const groupHealthScoreSchema = z
  .object({
    groupId: z.string(),
    /** Tên nhóm — hiển thị (không phải PII cá nhân; owner danh tính mới redact). */
    groupName: z.string(),
    /** Điểm tổng hợp [0..100] (số nguyên). */
    score: z.number().int().min(0).max(100),
    /** Bậc dẫn xuất từ score theo ngưỡng config. */
    tier: groupHealthTierSchema,
    /** Trục engagement [0..100]: % member active có session gần đây. */
    engagement: z.number().int().min(0).max(100),
    /** Trục financial [0..100]: độ tươi giao dịch ledger. */
    financial: z.number().int().min(0).max(100),
    /** Trục member growth [0..100]: Δ member active cửa sổ. */
    memberGrowth: z.number().int().min(0).max(100),
    /** Xu hướng score kỳ này vs kỳ trước, deltaPct (%) — có thể âm (giảm). */
    trendPct: z.number(),
    /** Số member active hiện tại (aggregate — luôn hiển thị). */
    activeMemberCount: z.number().int().min(0),
    /** Σ số dư CASH các fund (BigInt minor → string) — null khi redact theo vai (money). */
    totalBalanceMinor: z.string().nullable(),
  })
  .strict();

export type GroupHealthScore = z.infer<typeof groupHealthScoreSchema>;

/** Response GET /admin/analytics/group-health — ranking nhóm theo score (cursor). */
export const groupHealthResponseSchema = z
  .object({
    data: z.array(groupHealthScoreSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type GroupHealthResponse = z.infer<typeof groupHealthResponseSchema>;

// ============================================================
// @UC9094 — Churn watchlist + suggested action (AC2/AC3)
// ============================================================

/** Lý do vào watchlist — enum cố định (KHÔNG cột DB; DERIVE on-the-fly theo ngưỡng config). */
export const churnReasonSchema = z.enum(["no_txn_x_days", "active_member_drop"]);
export type ChurnReason = z.infer<typeof churnReasonSchema>;

/** Mức rủi ro churn — dẫn xuất (critical/high/medium). */
export const churnRiskLevelSchema = z.enum(["critical", "high", "medium"]);
export type ChurnRiskLevel = z.infer<typeof churnRiskLevelSchema>;

/**
 * Danh tính owner (redact theo vai). {userId, name} khi vai xem PII; `null` khi redact (UI "—").
 */
export const ownerRefSchema = z
  .object({
    userId: z.string(),
    /** fullName owner (có thể null nếu chưa đặt tên). */
    name: z.string().nullable(),
  })
  .strict();

export type OwnerRef = z.infer<typeof ownerRefSchema>;

/**
 * Hành động gợi ý (@UC9094 AC3) — DEEP-LINK ONLY. type ∈ {broadcast_reminder, contact_owner}. deepLink =
 * URL nội bộ tới màn AF13.1 broadcast / group detail. KHÔNG payload gửi; board KHÔNG dispatch (no-send).
 * ownerRef chỉ có ở contact_owner (redact theo vai).
 */
export const suggestedActionSchema = z
  .object({
    type: z.enum(["broadcast_reminder", "contact_owner"]),
    /** Deep-link nội bộ (relative). Mở màn để NGƯỜI thao tác — KHÔNG tự gửi. */
    deepLink: z.string(),
    /** Danh tính owner (chỉ contact_owner) — null khi redact theo vai. */
    ownerRef: ownerRefSchema.nullable().optional(),
  })
  .strict();

export type SuggestedAction = z.infer<typeof suggestedActionSchema>;

/**
 * Mục watchlist churn (@UC9094 AC2/AC3). reason DERIVE theo ngưỡng config (no_txn/active_drop).
 * daysSinceLastTxn / activeMemberDeltaPct = số liệu chứng minh (aggregate). suggestedActions deep-link only.
 */
export const churnWatchItemSchema = z
  .object({
    groupId: z.string(),
    groupName: z.string(),
    /** Lý do vào watchlist (DERIVE, không phải cột DB). */
    reason: churnReasonSchema,
    /** Mức rủi ro dẫn xuất. */
    riskLevel: churnRiskLevelSchema,
    /** Số ngày kể từ giao dịch cuối (reason=no_txn_x_days) — null nếu chưa từng có txn. */
    daysSinceLastTxn: z.number().int().min(0).nullable().optional(),
    /** % thay đổi member active cửa sổ vs trước (reason=active_member_drop) — âm = giảm. */
    activeMemberDeltaPct: z.number().nullable().optional(),
    /** Hành động gợi ý — deep-link only (AC3). */
    suggestedActions: z.array(suggestedActionSchema),
  })
  .strict();

export type ChurnWatchItem = z.infer<typeof churnWatchItemSchema>;

/** Response GET /admin/analytics/churn-watchlist — nhóm at-risk (cursor). */
export const churnWatchlistResponseSchema = z
  .object({
    data: z.array(churnWatchItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type ChurnWatchlistResponse = z.infer<typeof churnWatchlistResponseSchema>;

/** Query GET group-health & churn-watchlist — cursor + limit.
 * `format=csv` ⇒ export CSV ranking group-health (US-AE-IMP.F5-0116 · @UC9146); vắng ⇒ JSON.
 * (churn-watchlist bỏ qua format — chỉ group-health serialize CSV.) */
export const groupHealthQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    format: z.enum(["csv"]).optional(),
  })
  .strict();

export type GroupHealthQuery = z.infer<typeof groupHealthQuerySchema>;
